<script setup lang="ts">
// The studio's hand on a connected caster (CasterStats, while one is
// connected): a cue on their screen (one caster or every caster on this
// node), their microphone muted, what they may send (bitrate, picture
// height, frame rate) capped, and, when they allow it, their camera and
// microphone switched. Commands go over the caster's own connection
// (internal/caster/control.go); each is an event on the input.
import { computed, ref, watchEffect } from 'vue'
import { api, type CasterControl, type CasterStats } from '../../api'
import { ago } from '../../util'
import { useNode } from '../../store'
import Meters from '../Meters.vue'

// part: which block of the caster panel this renders (CasterStats lays them
// out): live (the microphone and cues) or sending (what the caster sends);
// session: the player it is for on a player input ("": the one on air)
const props = defineProps<{
    st: CasterStats
    inputId: string
    part: 'live' | 'sending'
    peak?: number[]
    session?: string
}>()
const node = useNode()
const cue = ref('')
const toAll = ref(false)
const busy = ref(false)

// each control's feedback: sending the request, then waiting until the
// caster's page reports the change (its status comes about once a second),
// then a tick for a moment; or why not
type What = 'cue' | 'mute' | 'limits' | 'cam' | 'mic'
const track = ref<{
    what: What
    at: number
    sent: boolean
    failed?: string
    check?: () => boolean
    okAt?: number
} | null>(null)
function stateOf(what: What): '' | 'sending' | 'waiting' | 'done' | 'late' | 'failed' {
    const t = track.value
    if (!t || t.what !== what) return ''
    if (t.failed) return node.now - t.at < 8000 ? 'failed' : ''
    if (!t.sent) return 'sending'
    if (!t.check || t.check()) return node.now - (t.okAt ?? node.now) < 3000 ? 'done' : ''
    return node.now - t.at < 6000 ? 'waiting' : 'late'
}
// when the caster's page confirmed it: the tick shows for a moment from then
watchEffect(() => {
    const t = track.value
    if (t && t.sent && !t.failed && !t.okAt && (!t.check || t.check())) track.value = { ...t, okAt: node.now }
})
const label = (what: What) =>
    ({
        '': '',
        sending: 'sending…',
        waiting: 'waiting for the caster…',
        done: what === 'cue' ? 'sent ✓' : 'done ✓',
        late: 'not confirmed by the caster yet',
        failed: track.value?.failed ?? 'failed',
    })[stateOf(what)]
// the control this panel's one status line speaks for: the last one used
// for picture caps and devices (the microphone and cue have their own)
const lastSent = computed<What>(() => {
    const w = track.value?.what
    return w === 'limits' || w === 'cam' || w === 'mic' ? w : 'limits'
})
const tone2 = (what: What) =>
    ({ '': '', sending: 'text-fg-2', waiting: 'text-fg-2', done: 'text-ok', late: 'text-warn', failed: 'text-bad' })[
        stateOf(what)
    ]

// every caster input on this node: "to every caster" sends to each
const casterInputs = computed(() => (node.config?.inputs ?? []).filter((i) => i.kind === 'caster'))
const page = computed(() => props.st.page)

async function send(c: CasterControl, ids = [props.inputId], what?: What, check?: () => boolean): Promise<boolean> {
    busy.value = true
    if (what) track.value = { what, at: node.now, sent: false, check }
    try {
        const res = await Promise.allSettled(
            ids.map((id) => api.casterControl(id, props.session ? { ...c, session: props.session } : c)),
        )
        const failed = res.flatMap((r, i) =>
            r.status === 'rejected'
                ? [`${inputName(ids[i]!)}: ${r.reason instanceof Error ? r.reason.message : r.reason}`]
                : [],
        )
        // "no caster is connected" on other inputs is expected when sending to all
        const real = ids.length > 1 ? failed.filter((f) => !f.endsWith('no caster is connected')) : failed
        if (real.length) node.notify(real.join('; '), true)
        const ok = real.length < ids.length
        if (what && track.value?.what === what)
            track.value = { ...track.value, sent: true, at: node.now, failed: ok ? undefined : real.join('; ') }
        return ok
    } finally {
        busy.value = false
    }
}
const inputName = (id: string) => node.config?.inputs.find((i) => i.id === id)?.name ?? id

async function sendCue() {
    const text = cue.value.trim()
    if (!text) return
    if (await send({ cue: text }, toAll.value ? casterInputs.value.map((i) => i.id) : undefined, 'cue')) cue.value = ''
}
function toggleMute() {
    const want = !page.value?.muted
    void send({ mute: want }, undefined, 'mute', () => page.value?.muted === want)
}

// the studio's caps: 0 is the input's own
const limits = computed(() => props.st.limits ?? {})
function setLimit(k: 'max_bitrate_k' | 'height' | 'fps', e: Event) {
    const v = Number((e.target as HTMLSelectElement).value)
    const want = { ...limits.value, [k]: v || undefined }
    const same = (a?: number, b?: number) => (a || 0) === (b || 0)
    void send({ limits: want }, undefined, 'limits', () => {
        const l = props.st.limits ?? {}
        return same(l.max_bitrate_k, want.max_bitrate_k) && same(l.height, want.height) && same(l.fps, want.fps)
    })
}
function device(k: 'cam' | 'mic', e: Event) {
    const id = (e.target as HTMLSelectElement).value
    void send({ [k]: id }, undefined, k, () => page.value?.[k] === id)
}
</script>

<template>
    <!-- live: the caster's microphone (level, mute), the cue box under it -->
    <div v-if="part === 'live'" data-test="caster-control">
        <div class="flex items-center gap-3" data-test="caster-meter">
            <Meters v-if="peak?.length" :db="peak.slice(0, 2)" :labels="['L', 'R']" size="full" />
            <div class="flex flex-col gap-1">
                <button
                    class="min-w-24 text-xs"
                    :class="page?.muted ? 'border-bad bg-bad font-semibold text-white hover:bg-bad' : ''"
                    :disabled="busy || !page"
                    :aria-busy="['sending', 'waiting'].includes(stateOf('mute'))"
                    @click="toggleMute"
                >
                    {{
                        ['sending', 'waiting'].includes(stateOf('mute'))
                            ? page?.muted
                                ? 'Unmuting…'
                                : 'Muting…'
                            : page?.muted
                              ? 'Unmute'
                              : 'Mute'
                    }}
                </button>
                <span
                    class="text-xs"
                    :class="
                        page?.talking ? 'font-semibold text-warn' : page?.muted ? 'font-semibold text-bad' : 'text-ok'
                    "
                    data-test="caster-mic"
                >
                    {{
                        page?.talking
                            ? 'talking back · off air'
                            : page?.muted
                              ? 'muted by ' + (page.muted_by === 'studio' ? 'the studio' : 'the caster')
                              : 'mic live'
                    }}
                </span>
                <span class="h-4 text-xs whitespace-nowrap" :class="tone2('mute')">{{
                    ['done', 'late', 'failed'].includes(stateOf('mute')) ? label('mute') : ''
                }}</span>
            </div>
        </div>
        <div class="mt-2 min-w-0">
            <form class="flex gap-1.5" @submit.prevent="sendCue">
                <input
                    v-model="cue"
                    maxlength="280"
                    placeholder="Cue on the caster's screen, e.g. “back in 30”"
                    aria-label="Cue"
                    class="min-w-0 flex-1 text-xs"
                />
                <button
                    class="shrink-0 btn-primary text-xs"
                    :disabled="busy || !cue.trim()"
                    :aria-busy="stateOf('cue') === 'sending'"
                >
                    {{ stateOf('cue') === 'sending' ? 'Sending…' : 'Send cue' }}
                </button>
            </form>
            <div class="mt-1 flex min-h-4 flex-wrap items-center gap-x-2 text-xs text-fg-2">
                <label v-if="casterInputs.length > 1" class="m-0 inline-flex items-center gap-1">
                    <input v-model="toAll" type="checkbox" class="w-auto" /> to every caster
                </label>
                <span v-if="label('cue')" :class="tone2('cue')" role="status">{{ label('cue') }}</span>
                <span v-else-if="st.cue" class="min-w-0 truncate"
                    >last: “{{ st.cue.text }}” {{ ago(st.cue.sent, node.now) }} ago ·
                    <span :class="st.cue.seen ? 'text-ok' : 'text-warn'">{{
                        st.cue.seen ? 'seen ✓' : 'not dismissed yet'
                    }}</span></span
                >
            </div>
        </div>
    </div>

    <!-- sending: caps on what the caster sends, their devices, their encoder -->
    <div v-else>
        <div class="grid grid-cols-3 gap-1.5 text-xs">
            <label class="m-0 text-xs text-fg-2"
                >bitrate
                <select :value="limits.max_bitrate_k ?? 0" :disabled="busy" @change="setLimit('max_bitrate_k', $event)">
                    <option :value="0">input's</option>
                    <option v-for="k in [8000, 6000, 4000, 2500, 1500, 800]" :key="k" :value="k">
                        {{ k / 1000 }} Mb/s
                    </option>
                </select></label
            >
            <label class="m-0 text-xs text-fg-2"
                >picture
                <select :value="limits.height ?? 0" :disabled="busy" @change="setLimit('height', $event)">
                    <option :value="0">input's</option>
                    <option v-for="h in [1080, 720, 540, 360]" :key="h" :value="h">{{ h }}p</option>
                </select></label
            >
            <label class="m-0 text-xs text-fg-2"
                >frame rate
                <select :value="limits.fps ?? 0" :disabled="busy" @change="setLimit('fps', $event)">
                    <option :value="0">input's</option>
                    <option v-for="f in [60, 50, 30, 25]" :key="f" :value="f">{{ f }} fps</option>
                </select></label
            >
        </div>
        <div v-if="page?.remote" class="mt-1.5 grid grid-cols-2 gap-1.5 text-xs">
            <label class="m-0 text-xs text-fg-2"
                >camera
                <select :value="page.cam" :disabled="busy" @change="device('cam', $event)">
                    <option v-for="d in page.cams ?? []" :key="d.id" :value="d.id">{{ d.label }}</option>
                </select></label
            >
            <label class="m-0 text-xs text-fg-2"
                >microphone
                <select :value="page.mic" :disabled="busy" @change="device('mic', $event)">
                    <option v-for="d in page.mics ?? []" :key="d.id" :value="d.id">{{ d.label }}</option>
                </select></label
            >
        </div>
        <!-- always one line, empty or not: a line coming and going moved
             everything under it (Niels) -->
        <div class="mt-0.5 h-4 truncate text-xs" :class="tone2(lastSent)" role="status" data-test="caster-sent">
            {{ label(lastSent) }}
        </div>
        <div class="mt-1.5 flex flex-wrap gap-x-3 text-xs text-fg-2">
            <span v-if="page?.encoder" title="the H.264 encoder the caster's browser uses"
                >encoder: {{ page.hw_encoder ? 'hardware' : 'software' }} · {{ page.encoder }}</span
            >
            <span v-if="page && !page.remote"
                >camera and microphone: the caster's to switch (a tick on their page lets you)</span
            >
        </div>
    </div>
</template>
